import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BACKEND_URL, fileUrl, fmtMoney } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Loader2, Plane, Hotel, CheckCircle2, Download, Sparkles, MapPin, Users, Check } from "lucide-react";
import { toast } from "sonner";
import { OptionBadge, optionColor } from "../components/quote/Badges";

const publicApi = `${BACKEND_URL}/api/public/quotes`;

const HotelPublicCard = ({ h, currency, canAccept, onAccept, accepting, selectMode, selected, onSelect, testId }) => {
  const img = (h.images || [])[0];
  const imgUrl = img ? (typeof img === "string" ? (img.startsWith("http") ? img : fileUrl(img)) : img.value) : null;
  const color = h.option_label ? optionColor(h.option_label) : "#0284C7";
  return (
    <Card
      className="rounded-2xl border-2 overflow-hidden bg-white transition-shadow"
      style={{
        borderColor: selected ? color : h.option_label ? color : "#E2E8F0",
        boxShadow: selected ? `0 0 0 3px ${color}44` : undefined,
      }}
      data-testid={testId}
    >
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-0">
        <div className="aspect-[4/3] md:aspect-auto md:h-full bg-slate-50 overflow-hidden">
          {imgUrl ? (
            <img src={imgUrl} alt={h.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300">
              <Hotel size={44} />
            </div>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {h.option_label && <OptionBadge label={`OPCIÓN ${h.option_label}`} />}
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              {h.nights} noche{h.nights !== 1 ? "s" : ""} · {h.room_type}
            </span>
          </div>
          <h3 className="font-serif-display text-2xl font-bold text-slate-900 leading-tight">{h.name}</h3>
          <p className="text-xs text-slate-500 mt-1">Régimen: {h.meal_plan}</p>
          <p className="text-sm text-slate-700 mt-2">{h.description}</p>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-slate-50 p-2.5">
              <p className="text-[10px] uppercase text-slate-400 tracking-widest">Por persona</p>
              <p className="text-lg font-bold text-amber-600 font-mono-2">{fmtMoney(h.price_per_person, currency)}</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-2.5">
              <p className="text-[10px] uppercase text-slate-400 tracking-widest">Total</p>
              <p className="text-lg font-bold text-amber-600 font-mono-2">{fmtMoney(h.price_total, currency)}</p>
            </div>
          </div>

          {selectMode ? (
            <Button
              onClick={onSelect}
              variant={selected ? "default" : "outline"}
              className={`mt-4 w-full rounded-xl h-11 font-semibold ${selected ? "text-white shadow-lg" : ""}`}
              style={selected ? { backgroundColor: color } : { borderColor: color, color }}
              data-testid={`${testId}-select-btn`}
            >
              {selected ? <Check size={16} className="mr-2" /> : <CheckCircle2 size={16} className="mr-2" />}
              {selected ? "Seleccionado" : "Elegir este hotel"}
            </Button>
          ) : (
            canAccept && (
              <Button
                onClick={() => onAccept(h.option_label)}
                disabled={accepting}
                className="mt-4 w-full rounded-xl h-11 text-white font-semibold shadow-lg"
                style={{ backgroundColor: color }}
                data-testid={`accept-option-${h.option_label || "single"}`}
              >
                {accepting ? <Loader2 className="animate-spin mr-2" size={16} /> : <CheckCircle2 size={16} className="mr-2" />}
                {h.option_label ? `Aceptar Opción ${h.option_label}` : "Aceptar esta opción"}
              </Button>
            )
          )}
        </div>
      </div>
    </Card>
  );
};

const PublicQuotePage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [expired, setExpired] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [chosenOption, setChosenOption] = useState("");
  const [acceptorName, setAcceptorName] = useState("");
  const [message, setMessage] = useState("");
  const [selection, setSelection] = useState({}); // nombre de grupo -> índice de hotel

  useEffect(() => {
    fetch(`${publicApi}/${token}`)
      .then((r) => {
        if (r.status === 410) { setExpired(true); return null; }
        if (!r.ok) return Promise.reject(r);
        return r.json();
      })
      .then((d) => { if (d) { setData(d); setAcceptorName(d.quote.passenger_name || ""); } })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const submitAccept = async () => {
    setAccepting(true);
    try {
      const res = await fetch(`${publicApi}/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accepted_by_name: acceptorName,
          accepted_option: chosenOption,
          accepted_options: chosenSummary.filter((s) => s.hotel).map((s) => `${s.group}: ${s.hotel}`),
          message,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success("¡Confirmación enviada!");
      const d = await fetch(`${publicApi}/${token}`).then((r) => r.json());
      setData(d);
      setShowAcceptForm(false);
    } catch { toast.error("No se pudo enviar la confirmación"); }
    finally { setAccepting(false); }
  };

  const openAcceptForm = (optionLabel) => {
    setChosenOption(optionLabel || "");
    setShowAcceptForm(true);
    setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }), 100);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;
  if (expired) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md text-center" data-testid="public-expired">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
          <Sparkles className="text-amber-500" size={28} />
        </div>
        <h1 className="font-serif-display text-3xl font-bold text-slate-900">Link expirado</h1>
        <p className="text-slate-600 mt-2">Este link de cotización ya venció. Contactá a tu agente de viajes para que te envíe uno nuevo con las tarifas actualizadas.</p>
      </div>
    </div>
  );
  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
          <Plane className="text-slate-400" size={28} />
        </div>
        <h1 className="font-serif-display text-3xl font-bold text-slate-900">Cotización no disponible</h1>
        <p className="text-slate-600 mt-2">El link puede haber expirado o el enlace fue revocado. Contactá a tu agente de viajes.</p>
      </div>
    </div>
  );

  const { quote, agency } = data;
  const currency = quote.currency || "USD";
  const passengers = quote.passengers || 1;
  const alreadyAccepted = quote.status === "accepted";
  const hotels = quote.hotels || [];
  const hasOptions = hotels.some((h) => h.option_label);
  const showTotal = quote.show_total !== false;
  const totalF = (quote.flights || []).filter((f) => f.show_price).reduce((s, f) => s + Number(f.price_total || 0), 0);

  // Agrupar hoteles por ciudad (conservando orden de aparición)
  const groupList = [];
  hotels.forEach((h, idx) => {
    const g = (h.group || "").trim();
    let grp = groupList.find((x) => x.name === g);
    if (!grp) { grp = { name: g, items: [] }; groupList.push(grp); }
    grp.items.push({ h, idx });
  });
  const namedGroups = groupList.filter((g) => g.name);
  const hasGroups = namedGroups.length > 0;
  const ungrouped = groupList.find((g) => !g.name);
  const selectionComplete = namedGroups.every((g) => selection[g.name] !== undefined);
  const selectedTotal = namedGroups.reduce((s, g) => {
    const si = selection[g.name];
    return si === undefined ? s : s + Number(hotels[si].price_total || 0);
  }, 0);
  const ungroupedTotal = (ungrouped?.items || []).reduce((s, it) => s + Number(it.h.price_total || 0), 0);
  const totalH = hasGroups ? selectedTotal + ungroupedTotal : hotels.reduce((s, h) => s + Number(h.price_total || 0), 0);
  const chosenSummary = namedGroups.map((g) => {
    const si = selection[g.name];
    return { group: g.name, hotel: si !== undefined ? hotels[si].name : "" };
  });
  const acceptedOptions = quote.accepted_options || [];
  const isAcceptedHotel = (h) => acceptedOptions.some((s) => String(s).endsWith(`: ${h.name}`));

  return (
    <div className="min-h-screen grain-bg" style={{ background: "var(--bg)" }}>
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {agency.logo_path ? (
              <img src={fileUrl(agency.logo_path)} alt="agency" className="h-10 w-auto object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-sky-600 flex items-center justify-center">
                <Plane className="text-white" size={20} />
              </div>
            )}
            {agency.agency_name && <p className="font-bold text-slate-900" style={{ color: agency.text_color }}>{agency.agency_name}</p>}
          </div>
          <a href={`${BACKEND_URL}/api/public/quotes/${token}/pdf`} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-xl" data-testid="public-download-pdf">
              <Download size={14} className="mr-2" /> Descargar PDF
            </Button>
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 pb-24">
        {/* Hero */}
        <div className="fade-up mb-8">
          <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: agency.accent_color }}>
            Cotización de viaje
          </p>
          <h1 className="font-serif-display text-4xl sm:text-6xl font-extrabold tracking-tight leading-tight" style={{ color: agency.text_color }}>
            {quote.destination}
          </h1>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-700">
            <span className="inline-flex items-center gap-2"><Users size={15} /> {quote.passenger_name} · {passengers} pasajero{passengers > 1 ? "s" : ""}</span>
            <span className="inline-flex items-center gap-2"><MapPin size={15} /> {quote.date}</span>
          </div>
        </div>

        {/* Acceptance banner if already accepted */}
        {alreadyAccepted && (
          <div className="fade-up rounded-2xl bg-emerald-50 border-2 border-emerald-200 p-5 mb-8 flex items-start gap-3" data-testid="accepted-banner">
            <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
              <CheckCircle2 size={20} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-emerald-900 text-lg">¡Cotización aceptada!</p>
              <p className="text-sm text-emerald-800">
                Aceptada por <b>{quote.accepted_by_name}</b>
                {!acceptedOptions.length && quote.accepted_option && <> · Opción elegida: <b>{quote.accepted_option}</b></>}
              </p>
              {acceptedOptions.length > 0 && (
                <ul className="text-sm text-emerald-800 mt-1 space-y-0.5" data-testid="accepted-options-list">
                  {acceptedOptions.map((o, i) => (
                    <li key={i} className="flex items-center gap-1.5"><Check size={13} /> <b>{o}</b></li>
                  ))}
                </ul>
              )}
              {quote.accepted_message && <p className="text-sm text-emerald-700 italic mt-1">"{quote.accepted_message}"</p>}
              <p className="text-xs text-emerald-600 mt-2">Tu agente de viajes se pondrá en contacto para continuar.</p>
            </div>
          </div>
        )}

        {/* Flights */}
        {(quote.flights || []).length > 0 && (
          <section className="fade-up mb-10">
            <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: agency.accent_color }}>Itinerario de vuelos</p>
            <h2 className="font-serif-display text-3xl font-bold text-slate-900 mb-4">Vuelos</h2>
            <div className="space-y-3">
              {quote.flights.map((f, i) => (
                <div key={i} className="rounded-xl bg-white border border-slate-200 p-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center">
                      <Plane className="text-sky-600" size={15} />
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-slate-900 font-mono-2">{f.origin}</span>
                      <span className="text-slate-300">→</span>
                      <span className="text-2xl font-bold text-slate-900 font-mono-2">{f.destination}</span>
                    </div>
                    <div className="text-xs text-slate-500 ml-auto flex flex-wrap gap-3">
                      <span>{f.date}</span>
                      <span>{f.departure_time} → {f.arrival_time}</span>
                      {f.baggage && <span>· {f.baggage}</span>}
                    </div>
                  </div>
                  {(f.airline || f.notes) && (
                    <p className="text-xs text-slate-500 mt-2 italic">{f.airline}{f.notes ? ` · ${f.notes}` : ""}</p>
                  )}
                  {f.show_price && (
                    <div className="mt-3 flex flex-wrap gap-4 text-sm border-t border-slate-100 pt-3" style={{ color: agency.price_color }}>
                      <span><b>Por persona:</b> {fmtMoney(f.price_per_person, currency)}</span>
                      <span><b>Total:</b> {fmtMoney(f.price_total, currency)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Hotels */}
        {hotels.length > 0 && (
          <section className="fade-up mb-10">
            <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: agency.accent_color }}>Alojamientos</p>
            <h2 className="font-serif-display text-3xl font-bold text-slate-900 mb-4">
              {hasGroups ? "Elegí un hotel por destino" : hasOptions ? "Elegí tu opción" : "Hotel"}
            </h2>
            {hasGroups && !alreadyAccepted && (
              <p className="text-sm text-slate-600 mb-4">Tocá "Elegir este hotel" en cada destino. El total se arma con tu selección.</p>
            )}
            {!hasGroups && hasOptions && !alreadyAccepted && (
              <p className="text-sm text-slate-600 mb-4">Cada opción tiene su color. Tocá "Aceptar" en la que prefieras.</p>
            )}

            {hasGroups ? (
              <div className="space-y-8">
                {namedGroups.map((g, gi) => (
                  <div key={g.name} data-testid={`hotel-group-${gi}`}>
                    <div className="flex items-center gap-2.5 mb-3">
                      <MapPin size={16} style={{ color: agency.accent_color }} />
                      <h3 className="font-serif-display text-2xl font-bold text-slate-900">{g.name}</h3>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 border border-slate-200 rounded-full px-2 py-0.5">Elegí 1</span>
                    </div>
                    <div className="space-y-4">
                      {g.items.map(({ h, idx }) => (
                        <HotelPublicCard
                          key={idx}
                          h={h}
                          currency={currency}
                          selectMode={!alreadyAccepted}
                          selected={alreadyAccepted ? isAcceptedHotel(h) : selection[g.name] === idx}
                          onSelect={() => setSelection((p) => ({ ...p, [g.name]: idx }))}
                          testId={`hotel-card-g${gi}-${idx}`}
                        />
                      ))}
                    </div>
                  </div>
                ))}
                {ungrouped && (
                  <div className="space-y-4" data-testid="hotel-group-ungrouped">
                    {ungrouped.items.map(({ h, idx }) => (
                      <HotelPublicCard key={idx} h={h} currency={currency} testId={`hotel-card-u-${idx}`} />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {hotels.map((h, i) => (
                  <HotelPublicCard
                    key={i}
                    h={h}
                    currency={currency}
                    canAccept={!alreadyAccepted}
                    onAccept={openAcceptForm}
                    accepting={accepting}
                    testId={`hotel-card-${i}`}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Selection summary CTA (grouped mode) */}
        {hasGroups && !alreadyAccepted && (
          <section className="fade-up rounded-2xl bg-white border-2 border-slate-200 p-6 mb-10" data-testid="selection-summary">
            <p className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: agency.accent_color }}>Tu selección</p>
            <ul className="space-y-1.5 mb-4">
              {chosenSummary.map((s) => (
                <li key={s.group} className="flex items-center justify-between gap-3 text-sm" data-testid={`selection-row-${s.group}`}>
                  <span className="font-semibold text-slate-700">{s.group}</span>
                  {s.hotel ? (
                    <span className="text-slate-900 flex items-center gap-1.5"><Check size={14} className="text-emerald-600" /> {s.hotel}</span>
                  ) : (
                    <span className="text-slate-400 italic">Pendiente de elección</span>
                  )}
                </li>
              ))}
            </ul>
            {showTotal && (
              <p className="text-lg font-bold font-mono-2 mb-4" style={{ color: agency.price_color }} data-testid="selection-total">
                Total seleccionado: {fmtMoney(totalH + totalF, currency)}
              </p>
            )}
            <Button
              onClick={() => openAcceptForm("")}
              disabled={!selectionComplete}
              className="w-full rounded-xl h-12 text-white font-bold shadow-lg"
              style={{ backgroundColor: agency.accent_color }}
              data-testid="accept-selection-btn"
            >
              <Sparkles size={17} className="mr-2" />
              {selectionComplete ? "Aceptar mi selección" : "Elegí un hotel en cada destino para continuar"}
            </Button>
          </section>
        )}

        {/* Total */}
        {showTotal && hotels.length > 0 && !hasGroups && (
          <section className="fade-up rounded-2xl p-6 mb-10" style={{ backgroundColor: "#121C2B" }}>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Total cotización</p>
            <p className="text-4xl sm:text-5xl font-bold font-mono-2 mt-1" style={{ color: agency.price_color }}>
              {fmtMoney(totalH + totalF, currency)}
            </p>
          </section>
        )}

        {/* Notes */}
        {quote.notes && (
          <section className="fade-up rounded-2xl bg-white border border-slate-200 p-5 mb-10">
            <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: agency.accent_color }}>Notas</p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{quote.notes}</p>
          </section>
        )}

        {/* Accept CTA (no hotels case or single option) */}
        {!alreadyAccepted && !hasOptions && !hasGroups && hotels.length > 0 && (
          <div className="text-center">
            <Button
              onClick={() => openAcceptForm("")}
              className="rounded-xl h-14 px-8 text-white font-bold text-lg shadow-xl"
              style={{ backgroundColor: agency.accent_color }}
              data-testid="accept-single-btn"
            >
              <Sparkles size={18} className="mr-2" /> Aceptar cotización
            </Button>
          </div>
        )}

        {/* Accept form */}
        {showAcceptForm && !alreadyAccepted && (
          <div className="fade-up rounded-2xl bg-white border-2 border-slate-200 p-6 mt-8" data-testid="accept-form">
            <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: agency.accent_color }}>Confirmar cotización</p>
            {hasGroups ? (
              <div className="mb-4">
                <h3 className="font-serif-display text-2xl font-bold text-slate-900 mb-2">Tu selección</h3>
                <ul className="space-y-1">
                  {chosenSummary.filter((s) => s.hotel).map((s) => (
                    <li key={s.group} className="text-sm text-slate-700 flex items-center gap-1.5">
                      <Check size={14} className="text-emerald-600" /> <b>{s.group}:</b> {s.hotel}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <h3 className="font-serif-display text-2xl font-bold text-slate-900 mb-4">
                {chosenOption ? <>Opción <span style={{ color: optionColor(chosenOption) }}>{chosenOption}</span></> : "Casi listo"}
              </h3>
            )}
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Tu nombre</label>
                <Input value={acceptorName} onChange={(e) => setAcceptorName(e.target.value)} placeholder="Tu nombre completo" className="rounded-xl h-11" data-testid="input-acceptor-name" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Mensaje (opcional)</label>
                <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Contale a tu agente si tenés alguna preferencia o pregunta..." className="rounded-xl" data-testid="input-acceptor-message" />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="ghost" onClick={() => setShowAcceptForm(false)}>Cancelar</Button>
                <Button
                  onClick={submitAccept}
                  disabled={accepting || !acceptorName.trim()}
                  className="text-white rounded-xl px-6"
                  style={{ backgroundColor: agency.accent_color }}
                  data-testid="submit-accept-btn"
                >
                  {accepting ? <Loader2 className="animate-spin mr-2" size={16} /> : <CheckCircle2 size={16} className="mr-2" />}
                  Confirmar
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-white px-6 py-4">
        <p className="max-w-4xl mx-auto text-xs text-slate-500 text-center italic">
          {agency.footer_text}
        </p>
      </footer>
    </div>
  );
};

export default PublicQuotePage;
